import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import Header from '../../components/Header';

const Pricing = () => {
  const [isYearly, setIsYearly] = useState(false);

  const handleContactUs = () => {
    if (typeof window !== 'undefined' && window.Tawk_API) {
      window.Tawk_API.maximize();
    }
  };

  const plans = [
    {
      name: 'Starter',
      cost: 0,
      credits: 5,
      term: 5,
      searches: 'Unlimited',
      profileView: 'Unlimited',
      aiInsights: 5,
      lists: 5,
      popular: false,
      features: [
        '5 Credits',
        '5 Days Access',
        'Unlimited Searches',
        'Unlimited Profile Views',
        '5 Days AI Insights',
        '5 Days Lists Access',
        'Basic Support',
      ],
      buttonText: 'Get Started Free',
      buttonVariant: 'outlined',
    },
    {
      name: 'Pro',
      cost: isYearly ? 1100 : 100,
      credits: isYearly ? 990 : 90,
      term: isYearly ? 365 : 30,
      searches: 'Unlimited',
      profileView: 'Unlimited',
      aiInsights: isYearly ? 365 : 30,
      lists: isYearly ? 365 : 30,
      popular: true,
      features: isYearly ? [
        '990 Credits',
        '365 Days Access',
        'Unlimited Searches',
        'Unlimited Profile Views',
        '365 Days AI Insights',
        '365 Days Lists Access',
        'Priority Support',
        'Advanced Analytics',
      ] : [
        '90 Credits',
        '30 Days Access',
        'Unlimited Searches',
        'Unlimited Profile Views',
        '30 Days AI Insights',
        '30 Days Lists Access',
        'Priority Support',
        'Advanced Analytics',
      ],
      buttonText: 'Get Pro',
      buttonVariant: 'contained',
    },
    {
      name: 'Enterprise',
      cost: 'Custom',
      credits: 'Unlimited',
      term: 'Custom',
      searches: 'Unlimited',
      profileView: 'Unlimited',
      aiInsights: 'Unlimited',
      lists: 'Unlimited',
      popular: false,
      features: [
        'Unlimited Credits',
        'Custom Duration',
        'Unlimited Searches',
        'Unlimited Profile Views',
        'Unlimited AI Insights',
        'Unlimited Lists Access',
        'Custom Integrations',
        'Team Management',
      ],
      buttonText: 'Contact Us',
      buttonVariant: 'outlined',
    },
  ];

  const getYearlyDiscount = (monthlyCost) => {
    if (monthlyCost === 0 || monthlyCost === 'Custom') return 0;
    return Math.round(monthlyCost * 0.2); // 20% discount
  };

  return (
    <Box sx={{ minHeight: '100vh', background: '#171717' }}>
      <Header />
      <Container maxWidth="xl" sx={{ py: 8 }}>
        {/* Header Section */}
        <Box textAlign="center" mb={8}>
          <Typography variant="h1" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' }, fontWeight: 800, mb: 2, background: 'linear-gradient(135deg, #1BE885 0%, #3b82f6 100%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'}} >
            Choose the plan that works for you
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1.25rem', color: '#B3B3B3', maxWidth: '600px', mx: 'auto', mb: 4 }}>
            Start with our free plan and upgrade as you grow. All plans include unlimited searches and profile views.
          </Typography>

          {/* Billing Toggle */}
          <Paper elevation={0} sx={{ display: 'inline-flex', p: 1, background: '#101010', border: '1px solid #27272a', borderRadius: 3, mb: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isYearly}
                  onChange={(e) => setIsYearly(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#1BE885',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#1BE885',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                  {isYearly ? 'Yearly' : 'Monthly'}
                </Typography>
              }
            />
          </Paper>
        </Box>

        {/* Pricing Cards */}
        <Grid container spacing={10} justifyContent="center">
          {plans.map((plan, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                maxWidth="30vw"
                elevation={0}
                sx={{
                  height: '100%',
                  background: plan.popular ? 'linear-gradient(135deg, #1BE885 0%, #3b82f6 100%)' : '#101010',
                  border: plan.popular ? '2px solid #1BE885' : '1px solid #27272a',
                  borderRadius: 4,
                  position: 'relative',
                  transform: plan.popular ? 'scale(1.05)' : 'scale(1)',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: plan.popular ? 'scale(1.07)' : 'scale(1.02)',
                  },
                }}
              >
                {plan.popular && (
                  <Chip
                    label="Most Popular"
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: '#1BE885',
                      color: '#000',
                      fontWeight: 700,
                      fontSize: '0.875rem',
                    }}
                  />
                )}

                <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {/* Plan Header */}
                  <Box textAlign="center" mb={3}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: plan.popular ? '#000' : '#fff',
                        mb: 1,
                      }}
                    >
                      {plan.name}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', mb: 1 }}>
                      <Typography
                        variant="h1"
                        sx={{
                          fontSize: '3rem',
                          fontWeight: 800,
                          color: plan.popular ? '#000' : '#fff',
                        }}
                      >
                        {plan.cost === 0 ? '$0' : plan.cost === 'Custom' ? 'Custom' : plan.cost === 'Contact' ? 'Contact' : `$${plan.cost}`}
                      </Typography>
                      {plan.cost !== 0 && plan.cost !== 'Custom' && plan.cost !== 'Contact' && (
                        <Typography
                          variant="body1"
                          sx={{
                            color: plan.popular ? '#000' : '#B3B3B3',
                            ml: 1,
                          }}
                        >
                          {isYearly ? '/year' : '/mo'}
                        </Typography>
                      )}
                    </Box>

                   
                  </Box>

                  <Divider sx={{ my: 3, borderColor: plan.popular ? '#000' : '#27272a' }} />

                  {/* Features List */}
                  <List sx={{ flexGrow: 1, mb: 3 }}>
                    {plan.features.map((feature, featureIndex) => (
                      <ListItem key={featureIndex} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckIcon
                            sx={{
                              color: plan.popular ? '#000' : '#1BE885',
                              fontSize: '1.25rem',
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          sx={{
                            '& .MuiListItemText-primary': {
                              color: plan.popular ? '#000' : '#fff',
                              fontSize: '0.95rem',
                              fontWeight: 500,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  {/* Action Button */}
                  <Button
                    component={plan.name === 'Enterprise' ? 'button' : RouterLink}
                    to={plan.name === 'Enterprise' ? undefined : '/register'}
                    onClick={plan.name === 'Enterprise' ? handleContactUs : undefined}
                    variant={plan.buttonVariant}
                    fullWidth
                    size="large"
                    sx={{
                      py: 2,
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      borderRadius: 2,
                      textTransform: 'none',
                      background: plan.popular ? '#000' : '#1BE885',
                      color: plan.popular ? '#1BE885' : '#000',
                      border: plan.popular ? '2px solid #000' : '2px solid #1BE885',
                      '&:hover': {
                        background: plan.popular ? '#1BE885' : 'transparent',
                        color: plan.popular ? '#000' : '#1BE885',
                        border: '2px solid #1BE885',
                      },
                    }}
                  >
                    {plan.buttonText}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* FAQ Section */}
        <Box sx={{ mt: 12, textAlign: 'center' }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              mb: 6,
              color: '#fff',
            }}
          >
            Frequently Asked Questions
          </Typography>

          <Grid container spacing={4} maxWidth="800px" mx="auto">
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  background: '#101010',
                  border: '1px solid #27272a',
                  borderRadius: 3,
                  height: '100%',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: '#fff', fontWeight: 600, mb: 2 }}
                >
                  What's included in the free plan?
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#B3B3B3', lineHeight: 1.6 }}
                >
                  The free plan includes 5 credits, 5 days of access, unlimited searches, unlimited profile views, and basic support to get you started.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  background: '#101010',
                  border: '1px solid #27272a',
                  borderRadius: 3,
                  height: '100%',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: '#fff', fontWeight: 600, mb: 2 }}
                >
                  What payment methods do you accept?
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#B3B3B3', lineHeight: 1.6 }}
                >
                  We accept all major credit cards, PayPal, and bank transfers for enterprise plans. All payments are processed securely.
                </Typography>
              </Paper>
            </Grid>

          </Grid>
        </Box>

        {/* CTA Section */}
        {/* <Box
          sx={{
            mt: 12,
            textAlign: 'center',
            p: 6,
            background: 'linear-gradient(135deg, #1BE885 0%, #3b82f6 100%)',
            borderRadius: 4,
            color: '#000',
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 2,
              color: '#000',
            }}
          >
            Ready to get started?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.25rem',
              mb: 4,
              color: '#000',
              opacity: 0.9,
            }}
          >
            Join thousands of users who trust GetLista for their professional networking needs.
          </Typography>
          <Button
            component={RouterLink}
            to="/register"
            variant="contained"
            size="large"
            sx={{
              px: 4,
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 700,
              borderRadius: 2,
              textTransform: 'none',
              background: '#000',
              color: '#1BE885',
              border: '2px solid #000',
              '&:hover': {
                background: 'transparent',
                color: '#000',
                border: '2px solid #000',
              },
            }}
          >
            Start Free Trial
          </Button>
        </Box> */}
      </Container>
    </Box>
  );
};

export default Pricing;
